import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  bakiyemListesi,
  izinGonder,
  izinIptal,
  izinlerimListesi,
  izinMudurOnayla,
  izinReddet,
  izinSefOnayla,
  onayBekleyenler,
  type IzinTalebiOzeti,
} from '../api/izin';
import type { Kimlik } from '../api/tipler';
import { GerekceDiyalogu } from '../bilesenler/Diyalog';
import { BakiyeListesi, DurumRozeti, SureHucresi } from '../bilesenler/IzinParcalari';
import { Tablo, type Sutun } from '../bilesenler/Tablo';
import { HataKutusu } from '../bilesenler/temel';
import { tarihMetni } from '../bicim/tarih';
import { yetkiKullan } from '../yetki/yetki';

/**
 * ONAY BEKLEYENLER VE KENDİ İZİNLERİM.
 *
 * ŞEF VE MÜDÜR ONAYI AYRI KODLAR: `IZIN_ONAY_ILK` şef basamağını,
 * `IZIN_ONAY_SON` müdür basamağını açıyor. Tek değişkenle yazılsaydı yalnız
 * şef yetkisi olan kullanıcı "Müdür onayı" düğmesine basıp 403 alırdı.
 *
 * Ekran her iki listeyi de gösteriyor ama DÜĞMELER AYRI: şef listesindeki
 * onay düğmesi `IZIN_ONAY_ILK`, müdür listesindeki `IZIN_ONAY_SON` istiyor.
 * Red düğmesi ikisinde de var (`IZIN_ONAY_ILK | IZIN_ONAY_SON`).
 */

/** Hangi eylem için diyalog açık. */
type Eylem =
  | { tur: 'sef-onay'; talep: IzinTalebiOzeti }
  | { tur: 'mudur-onay'; talep: IzinTalebiOzeti }
  | { tur: 'red'; talep: IzinTalebiOzeti }
  | { tur: 'iptal'; talep: IzinTalebiOzeti };

export function IzinOnay({ yetkilerHazir }: { yetkilerHazir: boolean }) {
  const sorguIstemcisi = useQueryClient();

  const sefOnaylayabilir = yetkiKullan('IZIN_ONAY_ILK');
  const mudurOnaylayabilir = yetkiKullan('IZIN_ONAY_SON');
  const reddedebilir = yetkiKullan('IZIN_ONAY_ILK', 'IZIN_ONAY_SON');

  const [eylem, eylemYaz] = useState<Eylem | null>(null);

  const bekleyenler = useQuery({
    queryKey: ['izin', 'onay-bekleyenler'],
    queryFn: () => onayBekleyenler(),
    enabled: yetkilerHazir,
  });

  const tazele = async () => {
    // Onay listeyi ve sayfalı listeyi birlikte değiştirir.
    await sorguIstemcisi.invalidateQueries({ queryKey: ['izin'] });
    eylemYaz(null);
  };

  const isle = useMutation({
    mutationFn: (girdi: { eylem: Eylem; metin: string }) => {
      const id: Kimlik = girdi.eylem.talep.izinTalebiId;

      switch (girdi.eylem.tur) {
        case 'sef-onay':
          // `aciklama` İSTEĞE BAĞLI — boşsa hiç gönderilmez.
          return izinSefOnayla(id, girdi.metin === '' ? undefined : girdi.metin);
        case 'mudur-onay':
          return izinMudurOnayla(id, girdi.metin === '' ? undefined : girdi.metin);
        case 'red':
          return izinReddet(id, girdi.metin);
        case 'iptal':
          return izinIptal(id, girdi.metin);
      }
    },
    onSuccess: tazele,
  });

  const sutunlariUret = (basamak: 'sef' | 'mudur'): Sutun<IzinTalebiOzeti>[] => [
    {
      baslik: 'Personel',
      hucre: (t) => (
        <span>
          <span className="block font-medium text-slate-900">{t.adSoyad}</span>
          <span className="block text-xs text-slate-500">
            {t.personelNo} · {t.birimAd}
          </span>
        </span>
      ),
    },
    { baslik: 'İzin tipi', hucre: (t) => t.izinTipiAd },
    {
      baslik: 'Tarih',
      hucre: (t) => `${tarihMetni(t.baslangicTarihi)} – ${tarihMetni(t.bitisTarihi)}`,
      daralt: true,
    },
    {
      baslik: 'Süre',
      hucre: (t) => <SureHucresi talep={t} />,
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: '',
      hucre: (t) => (
        <span className="flex justify-end gap-1 whitespace-nowrap">
          {/*
            ONAY DÜĞMESİ BASAMAĞIN KENDİ YETKİSİNE BAKAR. Tek değişken
            kullanılsaydı yalnız `IZIN_ONAY_ILK` olan kullanıcı müdür
            listesindeki düğmeyi görür ve 403 alırdı.
          */}
          {basamak === 'sef' && sefOnaylayabilir && (
            <Eylemci etiket="Şef onayı" tikla={() => eylemYaz({ tur: 'sef-onay', talep: t })} />
          )}
          {basamak === 'mudur' && mudurOnaylayabilir && (
            <Eylemci etiket="Müdür onayı" tikla={() => eylemYaz({ tur: 'mudur-onay', talep: t })} />
          )}
          {reddedebilir && (
            <Eylemci etiket="Reddet" tehlike tikla={() => eylemYaz({ tur: 'red', talep: t })} />
          )}
        </span>
      ),
      hizala: 'sag',
      daralt: true,
    },
  ];

  return (
    <div className="space-y-6">
      <HataKutusu hata={bekleyenler.error} />

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-800">Şef onayı bekleyenler</h2>
        <p className="text-xs text-slate-500">
          Bu basamak yalnız eşzamanlı izin sınırı aşıldığında devreye girer; aksi hâlde aynı gün tüm
          ekip izinli olabilirdi.
        </p>
        <Tablo
          sutunlar={sutunlariUret('sef')}
          satirlar={bekleyenler.data?.sefOnayi ?? []}
          anahtar={(t) => t.izinTalebiId}
          bekliyor={bekleyenler.isPending}
          bosMetin="Şef onayı bekleyen talep yok."
        />
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-800">Müdür onayı bekleyenler</h2>
        <Tablo
          sutunlar={sutunlariUret('mudur')}
          satirlar={bekleyenler.data?.mudurOnayi ?? []}
          anahtar={(t) => t.izinTalebiId}
          bekliyor={bekleyenler.isPending}
          bosMetin="Müdür onayı bekleyen talep yok."
        />
      </section>

      {eylem !== null && (
        <EylemDiyalogu
          eylem={eylem}
          bekliyor={isle.isPending}
          hata={isle.error}
          kapat={() => eylemYaz(null)}
          onayla={(metin) => isle.mutate({ eylem, metin })}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

const EYLEM_METNI: Record<
  Eylem['tur'],
  {
    baslik: string;
    etiket: string;
    onay: string;
    zorunlu: boolean;
    tehlike: boolean;
    ipucu: string;
  }
> = {
  'sef-onay': {
    baslik: 'Şef onayı ver',
    etiket: 'Açıklama',
    onay: 'Onayla',
    // `OnayDto.aciklama` @IsOptional — red ve iptalin aksine ZORUNLU DEĞİL.
    zorunlu: false,
    tehlike: false,
    ipucu: 'İsteğe bağlı — denetim izine yazılır.',
  },
  'mudur-onay': {
    baslik: 'Müdür onayı ver',
    etiket: 'Açıklama',
    onay: 'Onayla',
    zorunlu: false,
    tehlike: false,
    ipucu: 'İsteğe bağlı — onay sonrası izin plana yansır.',
  },
  red: {
    baslik: 'Talebi reddet',
    etiket: 'Red gerekçesi',
    onay: 'Reddet',
    zorunlu: true,
    tehlike: true,
    ipucu: 'Zorunlu — kayıt silinmez, gerekçe personele gösterilir.',
  },
  iptal: {
    baslik: 'Talebi iptal et',
    etiket: 'İptal gerekçesi',
    onay: 'İptal et',
    zorunlu: true,
    tehlike: true,
    ipucu: 'Zorunlu — BAŞLAMIŞ izin iptal edilemez.',
  },
};

function EylemDiyalogu({
  eylem,
  bekliyor,
  hata,
  kapat,
  onayla,
}: {
  eylem: Eylem;
  bekliyor: boolean;
  hata: unknown;
  kapat: () => void;
  onayla: (metin: string) => void;
}) {
  const m = EYLEM_METNI[eylem.tur];
  const t = eylem.talep;

  return (
    <GerekceDiyalogu
      baslik={m.baslik}
      aciklama={`${t.adSoyad} · ${t.izinTipiAd} · ${tarihMetni(t.baslangicTarihi)} – ${tarihMetni(t.bitisTarihi)} · ${t.isGunuSayisi} iş günü`}
      etiket={m.etiket}
      ipucu={m.ipucu}
      onayEtiketi={m.onay}
      onayTuru={m.tehlike ? 'tehlike' : 'birincil'}
      zorunluMu={m.zorunlu}
      enCokKarakter={500}
      bekliyor={bekliyor}
      hata={hata}
      kapat={kapat}
      onayla={(metin) => onayla(metin)}
    />
  );
}

function Eylemci({
  etiket,
  tikla,
  tehlike = false,
}: {
  etiket: string;
  tikla: () => void;
  tehlike?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={tikla}
      className={`rounded px-2 py-1 text-xs ${
        tehlike ? 'text-red-700 hover:bg-red-50' : 'text-kurum-700 hover:bg-kurum-50'
      }`}
    >
      {etiket}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Kendi izinlerim                                                     */
/* ------------------------------------------------------------------ */

/**
 * KENDİ İZİNLERİM.
 *
 * `benim` ve `bakiye/benim` AYRI UÇLARDIR ve ikisi de `IZIN_TALEP` istiyor —
 * `bakiye/:personelId` `PERSONEL_GORUNTULE`/`IZIN_ONAY_SON` istediği için
 * sıradan personel kendi bakiyesini oradan göremiyordu.
 *
 * TALEP AÇMA BURADA YOK: `POST /izin` `izinTipiId` istiyor ve izin tipi
 * kataloğu için uç bulunmuyor (bkz. `api/izin.ts`). Taslak PWA'dan açılır,
 * buradan GÖNDERİLEBİLİR — gönderme `izinTipiId` istemiyor.
 */
export function Izinlerim({ yetkilerHazir }: { yetkilerHazir: boolean }) {
  const sorguIstemcisi = useQueryClient();
  const [iptalEdilen, iptalEdilenYaz] = useState<IzinTalebiOzeti | null>(null);

  const liste = useQuery({
    queryKey: ['izin', 'benim'],
    queryFn: () => izinlerimListesi(),
    enabled: yetkilerHazir,
  });

  const bakiye = useQuery({
    queryKey: ['izin', 'bakiyem'],
    queryFn: () => bakiyemListesi(),
    enabled: yetkilerHazir,
  });

  const gonder = useMutation({
    mutationFn: (id: Kimlik) => izinGonder(id),
    onSuccess: () => sorguIstemcisi.invalidateQueries({ queryKey: ['izin'] }),
  });

  const iptal = useMutation({
    mutationFn: (girdi: { id: Kimlik; gerekce: string }) => izinIptal(girdi.id, girdi.gerekce),
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['izin'] });
      iptalEdilenYaz(null);
    },
  });

  const sutunlar: Sutun<IzinTalebiOzeti>[] = [
    { baslik: 'İzin tipi', hucre: (t) => t.izinTipiAd },
    {
      baslik: 'Tarih',
      hucre: (t) => `${tarihMetni(t.baslangicTarihi)} – ${tarihMetni(t.bitisTarihi)}`,
      daralt: true,
    },
    {
      baslik: 'Süre',
      hucre: (t) => <SureHucresi talep={t} />,
      daralt: true,
      hizala: 'sag',
    },
    { baslik: 'Durum', hucre: (t) => <DurumRozeti durum={t.durum} />, daralt: true },
    {
      baslik: '',
      hucre: (t) => (
        <span className="flex justify-end gap-1 whitespace-nowrap">
          {/*
            GÖNDER YALNIZ TASLAKTA. Taslak plana YANSIMAZ; gönderilmeden onay
            zincirine girmez ve personel izinli sayılmaz.
          */}
          {t.durum === 'taslak' && (
            <Eylemci etiket="Gönder" tikla={() => gonder.mutate(t.izinTalebiId)} />
          )}
          {(t.durum === 'taslak' || t.durum === 'sef_onayi' || t.durum === 'mudur_onayi') && (
            <Eylemci etiket="İptal" tehlike tikla={() => iptalEdilenYaz(t)} />
          )}
        </span>
      ),
      hizala: 'sag',
      daralt: true,
    },
  ];

  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-sm font-semibold text-slate-800">İzin bakiyem</h2>
        <HataKutusu hata={bakiye.error} />
        {bakiye.isPending ? (
          <p className="mt-1 text-sm text-slate-500">Yükleniyor…</p>
        ) : (
          <BakiyeListesi satirlar={bakiye.data ?? []} />
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-800">Taleplerim</h2>
        <HataKutusu hata={liste.error} />
        <HataKutusu hata={gonder.error} />

        {gonder.data && (
          /*
            GÖNDERME SONUCU YENİ DURUMU TAŞIR: sınır aşımı varsa `sef_onayi`,
            yoksa doğrudan `mudur_onayi`. Gösterilmezse kullanıcı hangi
            basamakta olduğunu bilemez.
          */
          <p className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            Talep gönderildi — şu an <DurumRozeti durum={gonder.data.durum} /> basamağında.
          </p>
        )}

        <Tablo
          sutunlar={sutunlar}
          satirlar={liste.data?.kayitlar ?? []}
          anahtar={(t) => t.izinTalebiId}
          bekliyor={liste.isPending}
          bosMetin="Henüz izin talebiniz yok."
        />
      </section>

      {iptalEdilen !== null && (
        <GerekceDiyalogu
          baslik="Talebi iptal et"
          aciklama={`${iptalEdilen.izinTipiAd} · ${tarihMetni(iptalEdilen.baslangicTarihi)} – ${tarihMetni(iptalEdilen.bitisTarihi)}`}
          etiket="İptal gerekçesi"
          ipucu="Zorunlu — BAŞLAMIŞ izin iptal edilemez."
          onayEtiketi="İptal et"
          onayTuru="tehlike"
          enCokKarakter={500}
          bekliyor={iptal.isPending}
          hata={iptal.error}
          kapat={() => iptalEdilenYaz(null)}
          onayla={(gerekce) => iptal.mutate({ id: iptalEdilen.izinTalebiId, gerekce })}
        />
      )}
    </div>
  );
}
